import React, { useEffect } from 'react';
import { Box, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from './PageHeader';
import BillingReminderBanner from './BillingReminderBanner';
import { useToast } from '../../context/ToastContext';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';

/** Flat brand band behind the page header, fading into the app background. */
export const managementPageBackground = `linear-gradient(180deg, ${BRAND.primary} 0, ${BRAND.primary} 260px, ${BRAND.background} 260px)`;

const managementPagePaddingX = { xs: 2, sm: 4, md: 6 };

const managementPageCardStyles = {
  borderRadius: BRAND_RADIUS.card,
  background: BRAND.surface,
  border: `1px solid ${BRAND.line}`,
  overflow: 'hidden',
  boxShadow: `0 16px 40px ${brandAlpha('primary', 0.1)}`,
};

export const ManagementPageCard = ({ id, children, elevation = 0, sx, ...props }) => (
  <Paper
    id={id}
    elevation={elevation}
    sx={{
      ...managementPageCardStyles,
      ...sx,
    }}
    {...props}
  >
    {children}
  </Paper>
);

const ManagementPageLayout = ({
  id,
  pageTitle,
  pageSubtitle,
  containerId,
  titleId,
  subtitleId,
  leadingContent,
  headerSx,
  headerMb = 4,
  contentId,
  contentSx,
  maxWidth = '1200px',
  animateHeader = true,
  sx,
  children,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { showInfo } = useToast();

  useEffect(() => {
    if (!location.state?.planUpgradeRequired) {
      return;
    }

    showInfo(t('storePlan.upgradeRequired'));
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate, showInfo, t]);
  const header = (pageTitle || pageSubtitle || leadingContent) ? (
    <PageHeader
      containerId={containerId}
      titleId={titleId}
      subtitleId={subtitleId}
      pageTitle={pageTitle}
      pageSubtitle={pageSubtitle}
      leadingContent={leadingContent}
      sx={headerSx}
      mb={headerMb}
    />
  ) : null;

  return (
    <Box
      id={id}
      sx={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        background: managementPageBackground,
        backgroundColor: BRAND.background,
        py: 4,
        px: managementPagePaddingX,
        ...sx,
      }}
    >
      <BillingReminderBanner maxWidth={maxWidth} />

      {header ? (
        animateHeader ? (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {header}
          </motion.div>
        ) : (
          header
        )
      ) : null}

      <Box
        id={contentId}
        sx={{
          width: '100%',
          maxWidth,
          mx: 'auto',
          ...contentSx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ManagementPageLayout;